import React, { useState, useRef, useEffect } from "react";
import { Box, Typography, IconButton, Button, TextField, Tooltip } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import Draggable from "react-draggable";
import SaveIcon from "@mui/icons-material/Save";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckIcon from "@mui/icons-material/Check";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import TextFieldsIcon from "@mui/icons-material/TextFields";

const fakeImageUrl = "https://via.placeholder.com/800x792.png?text=Fake+Document+Image";

const FloatingDocumentEditor = ({ imageSrc, fileType, onClose }) => {
    const theme = useTheme();
    const [currentPage, setCurrentPage] = useState(1);
    const [annotationMode, setAnnotationMode] = useState(false);
    const [newAnnotation, setNewAnnotation] = useState("");
    const [pages, setPages] = useState([{ src: imageSrc || fakeImageUrl, type: fileType || "image", annotations: [] }]);
    const imageRef = useRef(null);

    useEffect(() => {
        document.getElementById("root")?.removeAttribute("aria-hidden");
    }, []);

    const totalPages = pages.length;

    const handlePreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    const handleNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }
    };

    const handleDeletePage = () => {
        if (totalPages > 1) {
            setPages(pages.filter((_, index) => index !== currentPage - 1));
            setCurrentPage(Math.max(1, currentPage - 1));
        } else {
            alert("Cannot delete the last page.");
        }
    };

    const handleToggleAnnotationMode = () => {
        setAnnotationMode(!annotationMode);
    };

    const handleAddAnnotation = (event) => {
        if (annotationMode && newAnnotation.trim() !== "" && imageRef.current) {
            const { left, top } = imageRef.current.getBoundingClientRect();
            const x = event.clientX - left;
            const y = event.clientY - top;

            const updatedPages = [...pages];
            updatedPages[currentPage - 1].annotations.push({ text: newAnnotation, x, y, id: Date.now() });
            setPages(updatedPages);
            setNewAnnotation("");
            setAnnotationMode(false);
        }
    };

    const handleDeleteAnnotation = (id) => {
        const updatedPages = [...pages];
        updatedPages[currentPage - 1].annotations = updatedPages[currentPage - 1].annotations.filter(a => a.id !== id);
        setPages(updatedPages);
    };

    const handleUploadPage = (event) => {
        const file = event.target.files[0];
        if (file) {
            const newFileType = file.type;
            const newPage = { src: URL.createObjectURL(file), type: newFileType, annotations: [] };
            setPages([...pages, newPage]);
        }
    };

    return (
        <Box sx={{ display: "flex", height: "100vh", justifyContent: "center", alignItems: "center" }}>
            <Box
                sx={{
                    backgroundColor: "#f0f0f0",
                    borderRadius: "8px",
                    boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.2)",
                    padding: "20px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    width: "fit-content",
                    position: "relative",
                }}
            >
                {/* Close Button */}
                <IconButton
                    onClick={onClose}
                    sx={{ position: "absolute", top: 10, right: 10 }}
                >
                    <CloseIcon />
                </IconButton>

                <Typography variant="h5" gutterBottom>
                    Document Editor
                </Typography>

                {/* Toolbar */}
                <Box sx={{ display: "flex", justifyContent: "space-around", marginBottom: "20px", width: "100%" }}>
                    <Tooltip title="Save Annotations">
                        <IconButton color="primary">
                            <SaveIcon />
                        </IconButton>
                    </Tooltip>

                    <Tooltip title="Delete Page">
                        <IconButton color="error" onClick={handleDeletePage}>
                            <DeleteIcon />
                        </IconButton>
                    </Tooltip>

                    <Tooltip title="Add Text Annotation">
                        <IconButton color="primary" onClick={handleToggleAnnotationMode}>
                            <TextFieldsIcon />
                        </IconButton>
                    </Tooltip>
                </Box>

                {/* Pagination */}
                <Box sx={{ display: "flex", justifyContent: "space-between", marginBottom: "20px", width: "100%" }}>
                    <Button
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        onClick={handlePreviousPage}
                        disabled={currentPage === 1}
                    >
                        Previous
                    </Button>
                    <Typography>{`Page ${currentPage} of ${totalPages}`}</Typography>
                    <Button
                        variant="outlined"
                        endIcon={<ArrowForwardIcon />}
                        onClick={handleNextPage}
                        disabled={currentPage === totalPages}
                    >
                        Next
                    </Button>
                </Box>

                {/* Document Display */}
                <Box
                    sx={{
                        position: "relative",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        border: "1px solid #ccc",
                        width: "795px",
                        height: "500px",
                        backgroundColor: "#fff",
                    }}
                    onClick={handleAddAnnotation}
                >
                    <img
                        ref={imageRef}
                        src={pages[currentPage - 1].src}
                        alt="Document"
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />

                    {/* Draggable Annotations */}
                    {pages[currentPage - 1].annotations.map((annotation) => (
                        <Draggable key={annotation.id}>
                            <Box
                                sx={{
                                    position: "absolute",
                                    top: annotation.y,
                                    left: annotation.x,
                                    backgroundColor: "rgba(255, 255, 0, 0.3)",
                                    padding: "4px",
                                    borderRadius: "4px",
                                    color: "#000",
                                    cursor: "move",
                                }}
                            >
                                <Typography>{annotation.text}</Typography>
                                <IconButton size="small" onClick={() => handleDeleteAnnotation(annotation.id)}>
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </Box>
                        </Draggable>
                    ))}
                </Box>

                {/* Annotation Input */}
                {annotationMode && (
                    <Box sx={{ display: "flex", gap: 1, marginTop: 2 }}>
                        <TextField
                            size="small"
                            label="Annotation Text"
                            value={newAnnotation}
                            onChange={(e) => setNewAnnotation(e.target.value)}
                        />
                        <Button variant="contained" onClick={() => setAnnotationMode(false)}>
                            Done
                        </Button>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default FloatingDocumentEditor;
